// 📄 components/Configuracion/ConfiguracionSunat.tsx
// Credenciales de SUNAT por cliente: mismo patrón simple que CIG
// (usuario/contraseña, con opción de guardarlas). Igual que CIG, nunca
// se leen/escriben directo a Firestore — todo pasa por onehubApi.
import { useEffect, useState } from "react";
import { RefreshCw, KeyRound, Save } from "lucide-react";
import { obtenerConfigCliente, guardarConfigCliente, SunatCredenciales } from "../../services/clienteConexionService.ts";
import FlujoAprobacionCredencial from "../credenciales/FlujoAprobacionCredencial.tsx";

export default function ConfiguracionSunat({ clienteId }: { clienteId: string }) {
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [tieneCredencialesGuardadas, setTieneCredencialesGuardadas] = useState(false);
  const [usuarioGuardado, setUsuarioGuardado] = useState("");
  const [usuario, setUsuario] = useState("");
  const [contraseña, setContraseña] = useState("");
  const [url, setUrl] = useState("");
  const [guardarCredenciales, setGuardarCredenciales] = useState(false);
  // Sube al guardar una contraseña nueva: el flujo de aprobación se recarga (cambia quién la guardó).
  const [versionCredencial, setVersionCredencial] = useState(0);

  useEffect(() => {
    if (!clienteId) { setLoading(false); return; }
    setLoading(true);
    obtenerConfigCliente<SunatCredenciales>(clienteId, "sunat")
      .then((config) => {
        setTieneCredencialesGuardadas(config.tieneCredencialesGuardadas);
        setUsuarioGuardado(config.usuario);
        setUrl(config.url || "");
      })
      .catch((err) => {
        console.error("Error cargando configuración SUNAT:", err);
        setError("No se pudo cargar la configuración guardada.");
      })
      .finally(() => setLoading(false));
  }, [clienteId]);

  const handleGuardar = async () => {
    setError(null);
    setSuccess(null);

    if (guardarCredenciales && (!usuario.trim() || !contraseña)) {
      setError("Ingresa usuario y contraseña para guardar las credenciales.");
      return;
    }

    setGuardando(true);
    try {
      await guardarConfigCliente(clienteId, "sunat", {
        usuario: guardarCredenciales ? usuario.trim() : undefined,
        contraseña: guardarCredenciales ? contraseña : undefined,
        url: url.trim() || undefined
      });

      if (guardarCredenciales) {
        setTieneCredencialesGuardadas(true);
        setUsuarioGuardado(usuario.trim());
        setUsuario("");
        setContraseña("");
        setGuardarCredenciales(false);
        setVersionCredencial((v) => v + 1);
      }
      setSuccess("Configuración guardada correctamente.");
    } catch (err: any) {
      setError(err.message || "Error al guardar la configuración.");
    } finally {
      setGuardando(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-xs">{success}</div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-blue-400" /> Credenciales SUNAT
        </h3>

        {tieneCredencialesGuardadas && (
          <p className="text-xs text-gray-400 bg-black/20 border border-white/10 rounded-lg p-3">
            Ya hay credenciales guardadas para este cliente ({usuarioGuardado}). Completa los campos de abajo solo si quieres reemplazarlas.
          </p>
        )}

        {tieneCredencialesGuardadas && (
          <FlujoAprobacionCredencial credencial={{ tipo: "sunat", clienteId }} version={versionCredencial} />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Usuario</label>
            <input type="text" className={inputClass} value={usuario} onChange={(e) => setUsuario(e.target.value)} placeholder="Usuario SUNAT" />
          </div>
          <div>
            <label className={labelClass}>Contraseña</label>
            <input type="password" className={inputClass} value={contraseña} onChange={(e) => setContraseña(e.target.value)} placeholder="••••••••" />
          </div>
        </div>

        <div>
          <label className={labelClass}>URL del Servicio SUNAT</label>
          <input
            type="text"
            className={inputClass}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://e-factura.sunat.gob.pe/...ws/services/..."
          />
          <p className="text-xs text-gray-500 mt-1">Endpoint del servicio de validación SUNAT para este cliente. Si se deja vacío, se usa el valor por defecto del sistema.</p>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={guardarCredenciales}
            onChange={(e) => setGuardarCredenciales(e.target.checked)}
            className="w-4 h-4 rounded border-white/10 bg-zinc-800 accent-blue-500"
          />
          Guardar estas credenciales para este cliente
        </label>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleGuardar}
          disabled={guardando}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-5 py-2.5 rounded-lg text-sm font-semibold text-[#fff] transition-all"
        >
          <Save className="w-4 h-4" />
          {guardando ? "Guardando..." : "Guardar configuración"}
        </button>
      </div>
    </div>
  );
}
